import React, { useState, useEffect } from "react";
import { Grid, Card, Avatar, Box, Chip, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import { api } from "common";
import { isIdVerified } from "common/src/other/idDocumentRejection";
import Userinfo from "../components/Userinfo";
import GoBackButton from "components/GoBackButton";
import { C2, C2_FONT } from "../components/Table/C2UserTable";

function UserInfoDetails(props) {
  const { data } = props;
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const navigate = useNavigate();
  const { fetchUsersOnce } = api;
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settingsdata.settings);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const [loading, setLoading] = useState(true);
  const [fleetAdminsObj, setFleetAdminsObj] = useState();
  const [role, setRole] = useState(null);
  const auth = useSelector((state) => state.auth);
  const { state } = useLocation();

  const isEqual = (obj1, obj2) => JSON.stringify(obj1) === JSON.stringify(obj2);

  useEffect(() => {
    if (auth.profile && auth.profile.usertype) {
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

  useEffect(() => {
    if (staticusers) {
      if (role === "admin" || role === "fleetadmin") {
        let arr = staticusers.filter((user) => user.usertype === "fleetadmin");
        let obj = {};
        for (let i = 0; i < arr.length; i++) {
          let user = arr[i];
          obj[user.id] = user.firstName + " " + user.lastName;
        }
        if (!isEqual(obj, fleetAdminsObj)) {
          setFleetAdminsObj(obj);
        }
      }
    }
  }, [staticusers, role, fleetAdminsObj]);

  useEffect(() => {
    if (data) {
      setLoading(false);
    }
  }, [data]);

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  const initials =
    `${data?.firstName?.slice(0, 1) || ""}${data?.lastName?.slice(0, 1) || ""}`.toUpperCase() ||
    "?";
  const fullName = [data?.firstName, data?.lastName].filter(Boolean).join(" ") || "—";
  const roleLabel =
    data?.usertype === "driver"
      ? t("driver") || "Conductor"
      : data?.usertype === "customer"
      ? t("customer") || "Cliente"
      : data?.usertype || "";
  const approved = !!data?.approved;
  const idVerified = isIdVerified(data);

  if (loading) return <CircularLoading />;

  return (
    <Card
      sx={{
        borderRadius: "20px",
        bgcolor: C2.white,
        minHeight: 100,
        mb: 2.5,
        overflow: "hidden",
        boxShadow: "0 8px 28px rgba(26,15,59,0.08)",
        border: `1px solid ${C2.border}`,
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${C2.deep} 0%, ${C2.violet} 55%, ${C2.bright} 100%)`,
          px: { xs: 2, md: 3 },
          pt: 2,
          pb: 3,
        }}
      >
        <GoBackButton
          isRTL={isRTL}
          onClick={() => {
            data?.usertype === "customer"
              ? navigate("/users/0", { state: { pageNo: state?.pageNo } })
              : navigate("/users/1", { state: { pageNo: state?.pageNo } });
          }}
          light
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            gap: 2.5,
            mt: 1,
            direction: isRTL === "rtl" ? "rtl" : "ltr",
          }}
        >
          {data.profile_image ? (
            <Box
              component="img"
              src={data.profile_image}
              alt={fullName}
              sx={{
                width: 112,
                height: 112,
                borderRadius: "50%",
                objectFit: "cover",
                border: "3px solid rgba(251,250,254,0.85)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
              }}
            />
          ) : (
            <Avatar
              sx={{
                width: 112,
                height: 112,
                fontFamily: C2_FONT,
                fontWeight: 700,
                fontSize: 32,
                bgcolor: "rgba(251,250,254,0.18)",
                color: C2.white,
                border: "3px solid rgba(251,250,254,0.5)",
              }}
            >
              {initials}
            </Avatar>
          )}
          <Box sx={{ textAlign: { xs: "center", sm: isRTL === "rtl" ? "right" : "left" }, flex: 1 }}>
            <Typography
              sx={{
                fontFamily: C2_FONT,
                fontWeight: 800,
                fontSize: { xs: 22, md: 26 },
                color: C2.white,
                letterSpacing: 0.2,
              }}
            >
              {fullName}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mt: 1.25,
                justifyContent: { xs: "center", sm: isRTL === "rtl" ? "flex-end" : "flex-start" },
              }}
            >
              {roleLabel ? (
                <Chip
                  label={roleLabel}
                  size="small"
                  sx={{
                    fontFamily: C2_FONT,
                    fontWeight: 700,
                    bgcolor: "rgba(251,250,254,0.16)",
                    color: C2.white,
                    border: "1px solid rgba(251,250,254,0.35)",
                  }}
                />
              ) : null}
              <Chip
                label={approved ? t("approved") || "Aprobada" : t("not_approved") || "Pendiente"}
                size="small"
                sx={{
                  fontFamily: C2_FONT,
                  fontWeight: 700,
                  bgcolor: approved ? "rgba(18,183,106,0.9)" : "rgba(255,222,89,0.92)",
                  color: approved ? C2.white : C2.sidebar,
                }}
              />
              {idVerified ? (
                <Chip
                  label={t("verified_badge") || "Verificado"}
                  size="small"
                  sx={{
                    fontFamily: C2_FONT,
                    fontWeight: 700,
                    bgcolor: "rgba(18,183,106,0.95)",
                    color: C2.white,
                    border: "1px solid rgba(251,250,254,0.35)",
                  }}
                />
              ) : null}
              {data?.cityName ? (
                <Chip
                  label={data.cityName}
                  size="small"
                  sx={{
                    fontFamily: C2_FONT,
                    fontWeight: 600,
                    bgcolor: "rgba(251,250,254,0.12)",
                    color: C2.white,
                  }}
                />
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: C2.bg }}>
        <Typography
          sx={{
            fontFamily: C2_FONT,
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: C2.muted,
            mb: 2,
          }}
        >
          {t("info") || "Información"}
        </Typography>
        <Grid container spacing={2} sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
          <Grid item xs={12} sm={6}>
            <Userinfo title="first_name" value={data?.firstName} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Userinfo title="last_name" value={data?.lastName} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Userinfo
              title="mobile"
              value={settings.AllowCriticalEditsAdmin ? data?.mobile : t("hidden_demo")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Userinfo
              title="email"
              value={settings.AllowCriticalEditsAdmin ? data?.email : t("hidden_demo")}
            />
          </Grid>
          {(data?.usertype === "driver" || data?.usertype === "customer") && (
            <Grid item xs={12} sm={6}>
              <Userinfo
                title="city"
                value={
                  data?.cityName
                    ? data.cityName
                    : data?.cityId
                    ? data.cityId
                    : t("no_data_available")
                }
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Userinfo
              title="verify_id"
              value={
                data?.verifyId
                  ? settings.AllowCriticalEditsAdmin
                    ? data.verifyId
                    : t("hidden_demo")
                  : t("no_data_available")
              }
            />
          </Grid>
          {(data?.usertype === "driver" || data?.usertype === "customer") && data?.fleetadmin ? (
            <Grid item xs={12}>
              <Userinfo
                title="fleetadmins"
                value={
                  (fleetAdminsObj && fleetAdminsObj[data?.fleetadmin]
                    ? fleetAdminsObj[data?.fleetadmin]
                    : "") +
                  (data?.fleetadmin
                    ? settings.AllowCriticalEditsAdmin
                      ? " ( " + data.fleetadmin + " ) "
                      : " ( " + t("hidden_demo") + " ) "
                    : t("no_data_available"))
                }
              />
            </Grid>
          ) : null}
        </Grid>
      </Box>
    </Card>
  );
}

export default UserInfoDetails;
